import React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
    <Typography variant='body2' color='text.secondary' sx={{ minWidth: 96, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant='body2' sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const RecentOrderInfoModal = ({ open, onClose, onExited, loading, error, itemNumber, orders = [] }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${theme.palette.primary.main}2e`,
          boxShadow: `0 14px 34px ${theme.palette.primary.main}1f`,
          backgroundImage: `linear-gradient(180deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 34%)`,
        },
      }}
    >
      <DialogTitle
        sx={{
          position: 'relative',
          pr: 6,
          pb: 1.2,
          borderBottom: `1px solid ${theme.palette.primary.main}26`,
          backgroundColor: `${theme.palette.primary.main}12`,
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
          Recent Order Details
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Item #{itemNumber || 'N/A'}
        </Typography>
        <IconButton
          aria-label='close'
          onClick={onClose}
          size='small'
          sx={{ position: 'absolute', top: 12, right: 12, p: 0.5 }}
        >
          <CloseIcon sx={{ color: theme.palette.delete.main, fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '18px !important', pb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : error ? (
          <Typography variant='body2' color='error'>
            {error}
          </Typography>
        ) : orders.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No recent order history found for this item.
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {orders.map((order, index) => (
              <Box
                key={`${order.invoice_number || 'invoice'}-${index}`}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.background.paper,
                  p: 1.25,
                }}
              >
                <Stack spacing={0.5}>
                  <Row label='Source' value={order.source || 'N/A'} />
                  <Row label='Type' value={order.type || 'N/A'} />
                  <Row label='Invoice #' value={order.invoice_number || 'N/A'} />
                  <Row label='PO #' value={order.po_number || 'N/A'} />
                  <Row label='Qty' value={order.qty ?? 'N/A'} />
                  <Row label='Date' value={order.date || 'N/A'} />
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecentOrderInfoModal;
